import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useBackgroundJobs } from '../context/BackgroundJobsContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const UserEvents = () => {
  const { addDeletionJob } = useBackgroundJobs();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [eventName, setEventName] = useState('');
  const [eventPin, setEventPin] = useState('');
  const [eventShowAllPhotoTab, setEventShowAllPhotoTab] = useState(true);
  const [error, setError] = useState('');
  const [changePinEvent, setChangePinEvent] = useState(null);
  const [changePinValue, setChangePinValue] = useState('');
  const [changePinError, setChangePinError] = useState('');
  const [deleteConfirmEvent, setDeleteConfirmEvent] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  // Banner state
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerInputRef = useRef(null);
  // Per-card banner state: { [eventId]: { uploading, error } }
  const [cardBanner, setCardBanner] = useState({});
  const cardBannerRefs = useRef({});

  useEffect(() => {
    fetchEvents();
    fetchMe();
  }, []);

  const fetchMe = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`);
      setSubscription(res.data.subscription ?? null);
    } catch (_) {}
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/events`);
      setEvents(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setError('');
    const name = eventName.trim();
    const pin = eventPin.replace(/\D/g, '');
    if (!name) { setError('Event name is required'); return; }
    if (pin.length !== 6) { setError('PIN must be exactly 6 digits'); return; }
    try {
      const res = await axios.post(`${API_URL}/events`, { name, pin, show_all_photo_tab: eventShowAllPhotoTab });
      const newEventId = res.data.id;
      // Upload optional banner
      if (bannerFile && newEventId) {
        setBannerUploading(true);
        try {
          const fd = new FormData();
          fd.append('banner', bannerFile);
          await axios.post(`${API_URL}/events/${newEventId}/banner`, fd);
        } catch (_) {}
        setBannerUploading(false);
      }
      setShowModal(false);
      setEventName('');
      setEventPin('');
      setEventShowAllPhotoTab(true);
      setBannerFile(null);
      setBannerPreview(null);
      fetchEvents();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create event');
    }
  };

  const handleBannerSelect = (file) => {
    if (!file) return;
    setBannerFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setBannerPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCardBannerUpload = async (eventId, file) => {
    if (!file) return;
    setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: true, error: null } }));
    try {
      const fd = new FormData();
      fd.append('banner', file);
      const res = await axios.post(`${API_URL}/events/${eventId}/banner`, fd);
      setEvents((prev) => prev.map((ev) => ev.id === eventId ? { ...ev, banner_image: res.data.banner_image } : ev));
      setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: false, error: null } }));
    } catch (err) {
      setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: false, error: err.response?.data?.error || 'Upload failed' } }));
    }
  };

  const handleCardBannerRemove = async (eventId) => {
    setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: true, error: null } }));
    try {
      await axios.delete(`${API_URL}/events/${eventId}/banner`);
      setEvents((prev) => prev.map((ev) => ev.id === eventId ? { ...ev, banner_image: null } : ev));
      setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: false, error: null } }));
    } catch (err) {
      setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: false, error: err.response?.data?.error || 'Remove failed' } }));
    }
  };

  const toggleUploadLink = async (eventId) => {
    try {
      const res = await axios.patch(`${API_URL}/events/${eventId}/upload-link/toggle`);
      setEvents(events.map(ev => ev.id === eventId ? { ...ev, upload_link_active: res.data.upload_link_active } : ev));
    } catch (err) { alert(err.response?.data?.error || 'Failed to toggle'); }
  };

  const toggleVerifyLink = async (eventId) => {
    try {
      const res = await axios.patch(`${API_URL}/events/${eventId}/verify-link/toggle`);
      setEvents(events.map(ev => ev.id === eventId ? { ...ev, verify_link_active: res.data.verify_link_active } : ev));
    } catch (err) { alert(err.response?.data?.error || 'Failed to toggle'); }
  };

  const toggleShowAllPhotoTab = async (ev) => {
    const next = !(ev.show_all_photo_tab === true || ev.show_all_photo_tab === 1);
    try {
      await axios.put(`${API_URL}/events/${ev.id}`, { show_all_photo_tab: next });
      setEvents(events.map(e => e.id === ev.id ? { ...e, show_all_photo_tab: next } : e));
    } catch (err) { alert(err.response?.data?.error || 'Failed to update'); }
  };

  const handleDeleteEvent = async () => {
    if (!deleteConfirmEvent) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API_URL}/events/${deleteConfirmEvent.id}`);
      const batchList = res.data.batches || [];
      batchList.forEach((b) => {
        addDeletionJob({
          batchId: b.id,
          total: b.photo_count ?? 0,
          label: `Batch #${b.batch_number}${b.event_name ? ' - ' + b.event_name : ''}`,
          eventName: b.event_name || null,
        });
      });
      setEvents(events.filter(ev => ev.id !== deleteConfirmEvent.id));
      setDeleteConfirmEvent(null);
      alert(res.data.batches_queued > 0
        ? `Event deleted. ${res.data.batches_queued} batch(es) are being deleted in the background.`
        : 'Event deleted.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();
    const pin = changePinValue.replace(/\D/g, '');
    if (pin.length !== 6) { setChangePinError('PIN must be exactly 6 digits'); return; }
    if (!changePinEvent) return;
    setChangePinError('');
    try {
      const res = await axios.put(`${API_URL}/events/${changePinEvent.id}`, { pin });
      setEvents(events.map(ev => ev.id === changePinEvent.id ? { ...ev, pin: res.data.pin } : ev));
      setChangePinEvent(null);
      setChangePinValue('');
      alert('PIN updated.');
    } catch (err) {
      setChangePinError(err.response?.data?.error || 'Failed to update PIN');
    }
  };

  const baseUrl = window.location.origin;
  const verifyShareUrl = (token) => `${baseUrl}/verify/${token}`;
  const verifyOgUrl = (token) => `${baseUrl}/preview/${token}`;
  const subAllowed = subscription?.allowed === true;

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading events…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Events</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {events.length === 0 ? 'No events yet' : `${events.length} event${events.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button
          onClick={() => { setShowModal(true); setError(''); setEventName(''); setEventPin(''); }}
          disabled={subscription != null && !subAllowed}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white text-sm shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg active:scale-95"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          New Event
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-rose-50 border border-rose-200 px-4 py-3 rounded-xl">
          <p className="text-rose-700 text-sm">{error}</p>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirmEvent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6">
              <div className="w-12 h-12 rounded-2xl bg-rose-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-1">Delete event?</h2>
              <p className="text-sm text-slate-500 mb-6">
                <strong className="text-slate-700">&ldquo;{deleteConfirmEvent.name}&rdquo;</strong> and all its {deleteConfirmEvent.batch_count || 0} batches ({deleteConfirmEvent.photo_count || 0} photos) will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setDeleteConfirmEvent(null)} disabled={deleting} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
                <button type="button" onClick={handleDeleteEvent} disabled={deleting} className="flex-1 px-4 py-2.5 bg-rose-600 text-white rounded-xl text-sm font-semibold hover:bg-rose-700 disabled:opacity-50 transition-colors">
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change PIN modal */}
      {changePinEvent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Change PIN</h2>
              <p className="text-sm text-slate-500 mb-4">{changePinEvent.name}</p>
              {changePinError && <p className="text-rose-600 text-sm mb-3 bg-rose-50 px-3 py-2 rounded-lg">{changePinError}</p>}
              <form onSubmit={handleChangePin} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">New 6-digit PIN</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={changePinValue}
                    onChange={(e) => setChangePinValue(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-center text-lg tracking-widest font-mono outline-none transition-shadow"
                    placeholder="123456"
                    autoFocus
                  />
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => { setChangePinEvent(null); setChangePinValue(''); setChangePinError(''); }} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>Update PIN</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Event modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="px-6 pt-6 pb-2">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #ede9fe, #e0e7ff)' }}>
                  <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Create Event</h2>
                  <p className="text-xs text-slate-500">Set up a new event with upload and sharing links</p>
                </div>
              </div>
              {error && <p className="text-rose-600 text-sm mb-4 bg-rose-50 px-3 py-2 rounded-lg">{error}</p>}
              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Event name</label>
                  <input
                    type="text"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none transition-shadow text-sm"
                    placeholder="e.g. Annual Day 2025"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    6-digit PIN <span className="font-normal text-slate-400">(for upload link)</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={eventPin}
                    onChange={(e) => setEventPin(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none transition-shadow text-center text-lg tracking-widest font-mono"
                    placeholder="123456"
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1.5">Guests will need this PIN to upload photos.</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="event-show-all-photo-tab"
                    checked={eventShowAllPhotoTab}
                    onChange={(e) => setEventShowAllPhotoTab(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  <label htmlFor="event-show-all-photo-tab" className="text-xs text-slate-600 cursor-pointer">
                    Show &quot;All Photos&quot; tab on the find-my-photos page — guests can browse all event photos (view only).
                  </label>
                </div>

                {/* Optional banner */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-semibold text-slate-700">
                      Event banner <span className="font-normal text-slate-400">(optional)</span>
                    </label>
                    {bannerPreview && (
                      <button type="button" onClick={() => { setBannerFile(null); setBannerPreview(null); }} className="text-xs text-rose-500 hover:text-rose-700">Remove</button>
                    )}
                  </div>
                  {bannerPreview ? (
                    <div className="relative rounded-xl overflow-hidden border border-slate-200 cursor-pointer" onClick={() => bannerInputRef.current?.click()}>
                      <img src={bannerPreview} alt="Banner preview" className="w-full h-32 object-cover" />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <span className="text-white text-xs font-semibold">Click to change</span>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => bannerInputRef.current?.click()}
                      className="w-full h-24 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-violet-400 hover:bg-violet-50/40 transition-colors text-slate-400 hover:text-violet-500"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs">Upload banner photo</span>
                    </button>
                  )}
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => handleBannerSelect(e.target.files?.[0])}
                  />
                  <p className="text-xs text-slate-400 mt-1">Shown on the upload &amp; find-photos pages. Max 10 MB.</p>
                </div>

                <div className="flex gap-3 pt-2 pb-4">
                  <button type="button" onClick={() => { setShowModal(false); setError(''); setBannerFile(null); setBannerPreview(null); }} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
                  <button type="submit" disabled={bannerUploading} className="flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-md active:scale-95 disabled:opacity-60" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                    {bannerUploading ? 'Creating…' : 'Create Event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Event list / empty state */}
      {events.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ede9fe, #e0e7ff)' }}>
            <svg className="w-8 h-8 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-slate-800 mb-1">No events yet</h3>
          <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">Create your first event to get an upload link for guests and a find-my-photos link to share with them.</p>
          <button
            onClick={() => { setShowModal(true); setError(''); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white text-sm shadow-md transition-all hover:shadow-lg active:scale-95"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Create your first event
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((ev) => {
            const hasPin = ev.pin != null && ev.pin !== '';
            const pendingCount = ev.pending_batch_count || 0;
            return (
              <div
                key={ev.id}
                onClick={() => navigate(`/events/${ev.id}`)}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-violet-200 transition-all duration-200 overflow-hidden flex flex-col cursor-pointer group"
              >
                {/* Banner */}
                {ev.banner_image ? (
                  <div className="relative overflow-hidden">
                    <img
                      src={ev.banner_image}
                      alt="Event banner"
                      className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {pendingCount > 0 && (
                      <div className="absolute top-2 right-2">
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full animate-pulse">
                          {pendingCount} pending
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-20 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ede9fe 0%, #e0e7ff 100%)' }}>
                    <svg className="w-8 h-8 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {pendingCount > 0 && (
                      <span className="absolute top-2 right-2 text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full animate-pulse">
                        {pendingCount} pending
                      </span>
                    )}
                  </div>
                )}

                {/* Card body */}
                <div className="px-4 py-3 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2 flex-1">{ev.name}</h3>
                    <svg className="w-4 h-4 text-slate-300 group-hover:text-violet-500 transition-colors flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  {ev.created_at && (
                    <p className="text-xs text-slate-400 mb-2">
                      {new Date(ev.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                    </p>
                  )}

                  {/* Stats row */}
                  <div className="flex items-center gap-1.5 flex-wrap mt-auto">
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{ev.batch_count || 0} batches</span>
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{ev.photo_count || 0} photos</span>
                    {(ev.face_verified_count ?? ev.verification_count ?? 0) > 0 && (
                      <span className="text-xs bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full">{ev.face_verified_count ?? ev.verification_count} verified</span>
                    )}
                    {hasPin && (
                      <span className="inline-flex items-center gap-1 bg-violet-50 border border-violet-200 text-violet-700 px-2 py-0.5 rounded-full text-xs font-mono font-semibold">
                        <svg className="w-3 h-3 text-violet-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        {String(ev.pin)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-400">
                    {pendingCount > 0 ? (
                      <span className="text-amber-600 font-semibold">{pendingCount} batch{pendingCount !== 1 ? 'es' : ''} need approval</span>
                    ) : (
                      'Tap to manage'
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirmEvent(ev); }}
                    className="p-1 rounded-lg text-slate-300 hover:bg-rose-50 hover:text-rose-500 transition-colors"
                    title="Delete event"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserEvents;
